import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Stack,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import DeleteIcon from '@/assets/svgs/garbage.svg';
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import { validateProduct } from './validation-alerts';
import ExpandedProductForm from './expanded-product-form';

interface ProductReviewCardProps {
  product: CreateProductInput;
  index: number;
  onUpdate: (index: number, updatedProduct: CreateProductInput) => void;
  onRemove: (index: number) => void;
  requiresMetalPercentage?: boolean;
  isSelected?: boolean;
}

const ProductReviewCard: React.FC<ProductReviewCardProps> = ({
  product,
  index,
  onUpdate,
  onRemove,
  requiresMetalPercentage,
  isSelected,
}) => {
  const [expanded, setExpanded] = useState(false);
  const issues = validateProduct(product, requiresMetalPercentage);
  
  const hasErrors = issues.some(issue => issue.type === 'error');
  const hasWarnings = issues.some(issue => issue.type === 'warning');

  const getStatusColor = () => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (hasErrors) return <ErrorIcon fontSize="small" />;
    if (hasWarnings) return <WarningIcon fontSize="small" />;
    return <CheckCircleIcon fontSize="small" />;
  };

  const getStatusText = () => {
    if (hasErrors) return 'Has Errors';
    if (hasWarnings) return 'Needs Attention';
    return 'Valid';
  };

  const handleCardClick = (event: React.MouseEvent) => {
    // Don't toggle if clicking on interactive elements or inside the expanded form
    const target = event.target as HTMLElement;
    if (target.closest('button') || 
        target.closest('input') || 
        target.closest('textarea') ||
        target.closest('select') || 
        target.closest('.MuiFormControl-root') ||
        target.closest('.MuiCollapse-root')) {
      return;
    }
    setExpanded(!expanded);
  };

  return (
    <Card 
      sx={{ 
        mb: 2,
        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
      }}
    >
      <CardContent 
        sx={{ 
          pb: 1,
          '&:hover': {
            backgroundColor: '#f9fafb',
            cursor: 'pointer'
          }
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h6" component="div">
                Row {index + 1}
              </Typography>
              <Chip
                size="small"
                label={getStatusText()}
                color={getStatusColor() as any}
                icon={getStatusIcon()}
              />
              {requiresMetalPercentage && (
                <Tooltip title="Metal percentage required">
                  <Chip
                    size="small"
                    label="Metal Required"
                    color="warning"
                    variant="outlined"
                    icon={<FactoryIcon fontSize="small" />}
                  />
                </Tooltip>
              )}
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  SKU
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.sku || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  HTS Code
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.htsCode || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.countryOfOrigin || '-'}
                </Typography>
              </Grid>
            </Grid>

            {product.metalPercentage !== undefined && product.metalPercentage !== null && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Metal Percentage
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.metalPercentage}%
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={issues.length} color={getStatusColor() as any} invisible={issues.length === 0}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Badge>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            >
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </CardContent>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent 
          sx={{ pt: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ExpandedProductForm
            product={product}
            index={index}
            onUpdate={onUpdate}
            requiresMetalPercentage={requiresMetalPercentage}
            issues={issues}
          />
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ProductReviewCard;